import {
  QUERY_NAME,
  SERVICES,
  WORKER_LOGTIME_TARGET_TAB,
} from "@lhd/constants";
import axios from "@lhd/libs/axios";
import axiosInstance from "@lhd/libs/axios";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import {
  CompanyPermission,
  CompanyPolicyPermissionInput,
  CompanyPolicyPermissionRecord,
  CurrentUserPermissions,
  InternalControlRecordPermissionsForm,
  WorkerPermissionsItem,
} from "@lhd/types";
import { getCompanyId, getCookie, getUid } from "@lhd/utility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { useGetAuth } from "./useGetAuth";

export const useGetCompanyPermissions = (
  page: number,
  pageSize: number,
  params?: any,
  isEnabled = true,
) => {
  const company_id = getCompanyId();

  return useQuery(
    [QUERY_NAME.COMPANY_PERMISSIONS, company_id, page, pageSize, params],
    () =>
      axios({
        method: "get",
        url: SERVICES.COMPANY_PERMISSIONS,
        params: {
          page,
          per_page: pageSize,
          company_id,
          ...params,
        },
      }).then((res) => ({
        data: res.data as CompanyPermission[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const usePostCompanyPermissions = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.COMPANY_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_PERMISSIONS]);
      },
    },
  );
};

export const useGetWorkerPermissions = (page: number, pageSize: number) => {
  const company_id = getCompanyId();

  return useQuery(
    [QUERY_NAME.WORKER_PERMISSIONS, company_id, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.WORKER_PERMISSIONS,
        params: {
          page,
          per_page: pageSize,
          company_id,
        },
      }).then((res) => ({
        data: res.data as WorkerPermissionsItem[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const usePostWorkerPermissions = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.WORKER_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.WORKER_PERMISSIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました",
        });
      },
    },
  );
};

export const useGetWorkerLogtimePermissions = (
  page = 1,
  pageSize = 1000000,
) => {
  const company_id = getCompanyId();

  return useQuery(
    [QUERY_NAME.LOGTIME_PERMISSIONS, company_id, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.WORKER_PERMISSIONS,
        params: {
          page,
          per_page: pageSize,
          company_id,
          target_tab: WORKER_LOGTIME_TARGET_TAB,
        },
      }).then((res) => ({
        data: res.data as WorkerPermissionsItem[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const usePostWorkerLogtimePermissions = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.WORKER_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.LOGTIME_PERMISSIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useGetOrgInCompanyPermissions = (
  positionIds?: string,
  workerIds?: string,
  name?: string,
  options?: any,
) => {
  const company_id = getCompanyId();

  return useQuery(
    [
      QUERY_NAME.ORG_IN_COMPANY_PERMISSIONS,
      company_id,
      name,
      positionIds,
      workerIds,
    ],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-orgs-and-nodes-full`,
        params: {
          company_id,
          page: 1,
          per_page: 10000,
          ...(name && { name }),
          ...(positionIds && { position_ids: positionIds }),
          ...(workerIds && { worker_ids: workerIds }),
        },
      });
      return {
        data: response.data as any,
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      ...options,
    },
  );
};

export const useGetPositionInCompanyPermissions = (
  orgNodeIds?: string,
  workerIds?: string,
  name?: string,
  options?: any,
) => {
  const company_id = getCompanyId();

  const normalizedOrgNodeIds = orgNodeIds ?? null;
  const normalizedWorkerIds = workerIds ?? null;
  const normalizedName = name ?? null;

  return useQuery(
    [
      QUERY_NAME.POSITION_IN_COMPANY_PERMISSIONS,
      company_id,
      normalizedOrgNodeIds,
      normalizedWorkerIds,
      normalizedName,
    ],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-positions`,
        params: {
          company_id,
          page: 1,
          per_page: 10000,
          ...(orgNodeIds && { org_node_ids: orgNodeIds }),
          ...(workerIds && { worker_ids: workerIds }),
          ...(name && { name }),
        },
      });
      return {
        data: response.data as any,
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useGetCompanyWorkers = (
  orgNodeIds?: string,
  positionIds?: string,
  options?: any,
  orgIds?: string,
) => {
  const companyId = getCompanyId();
  let mergedOrgNodeIds = orgNodeIds || "";
  if (orgIds) {
    const orgNodeIdsArr = orgNodeIds ? orgNodeIds.split(",") : [];
    const orgIdsArr = orgIds ? orgIds.split(",") : [];
    mergedOrgNodeIds = Array.from(new Set([...orgNodeIdsArr, ...orgIdsArr]))
      .filter(Boolean)
      .join(",");
  }
  return useQuery(
    [
      QUERY_NAME.COMPANY_WORKER_PERMISSIONS,
      companyId,
      mergedOrgNodeIds,
      positionIds,
      orgIds,
    ],
    async () => {
      const response = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-workers`,
        params: {
          company_id: companyId,
          page: 1,
          per_page: 10000,
          ...(mergedOrgNodeIds && { org_node_ids: mergedOrgNodeIds }),
          ...(positionIds && { position_ids: positionIds }),
          ...(orgIds && { org_ids: orgIds }),
        },
      });
      return {
        data: response.data as any,
      };
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );
};

export const useCurrentUserPermissions = (
  targetTab: string,
  isEnabled = true,
) => {
  const company_id = getCompanyId();
  const uid = getUid();
  const { isUserResigned } = useGetAuth();
  const accessToken = getCookie("access_token") || "";
  return useQuery(
    [QUERY_NAME.CURRENT_USER_PERMISSIONS, company_id, targetTab, uid],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.CURRENT_USER_PERMISSIONS,
        params: {
          target_tab: targetTab,
          company_id,
        },
      }).then((res) => ({
        data: res.data as CurrentUserPermissions,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      staleTime: Infinity,
      enabled: isEnabled && !isUserResigned && !!accessToken,
    },
  );
};

export const useCompanyPolicyPermissions = (
  appId: number | string,
  page: number,
  pageSize: number,
) => {
  const company_id = getCompanyId();
  return useQuery(
    [QUERY_NAME.COMPANY_POLICY_PERMISSIONS, company_id, appId, page, pageSize],
    () =>
      axiosInstance({
        method: "get",
        url: SERVICES.COMPANY_POLICY_PERMISSIONS,
        params: {
          policy_app_id: appId,
          login_company_id: company_id,
          page,
          per_page: pageSize,
        },
      }).then((res) => ({
        data: res.data as CompanyPolicyPermissionRecord[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!appId,
    },
  );
};

export const usePostCompanyPolicyPermissions = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    (payload: { payload: CompanyPolicyPermissionInput }) =>
      axiosInstance({
        method: "post",
        url: SERVICES.COMPANY_POLICY_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_POLICY_PERMISSIONS]);
      },
    },
  );
};

// for role permissions
export const useGetRolePermissions = (isEnabled = true) => {
  const company_id = getCompanyId();

  return useQuery(
    [QUERY_NAME.ROLE_PERMISSIONS, company_id],
    () =>
      axios({
        method: "GET",
        url: SERVICES.COMPANY_PERMISSIONS,
        params: {
          company_id,
          target_tab: 10,
        },
      }).then((res) => ({
        data: res.data as CompanyPermission[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};
export const usePostInternalControlPermissions = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.COMPANY_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.ROLE_PERMISSIONS]);
      },
    },
  );
};

//
export const useGetAllPermissions = (isEnabled = true) => {
  const company_id = getCompanyId();

  return useQuery(
    [QUERY_NAME.ROLE_PERMISSIONS, company_id],
    () =>
      axios({
        method: "GET",
        url: SERVICES.COMPANY_PERMISSIONS,
        params: {
          company_id,
          page: 1,
          per_page: 10000,
        },
      }).then((res) => ({
        data: res.data as CompanyPermission[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetInternalControlRecordPermissionByTab = (
  page: number,
  pageSize: number,
  tab: string,
) => {
  const company_id = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.INTERNAL_CONTROL_RECORD_PERMISSIONS,
      company_id,
      page,
      pageSize,
      tab,
    ],
    () =>
      axiosInstance({
        method: "get",
        url: SERVICES.INTERNAL_CONTROL_RECORD_PERMISSIONS,
        params: {
          login_company_id: company_id,
          page,
          per_page: pageSize,
          target_tab: tab,
        },
      }).then((res) => ({
        data: res.data as InternalControlRecordPermissionsForm[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!tab,
    },
  );
};

export const usePostInternalControlRecordPermissionByTab = () => {
  const queryClient = useQueryClient();
  const login_company_id = getCompanyId();

  return useMutation(
    (payload: { payload: CompanyPolicyPermissionInput }) =>
      axiosInstance({
        method: "post",
        url: SERVICES.INTERNAL_CONTROL_RECORD_PERMISSIONS + "/bulk",
        data: { ...payload, login_company_id },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.INTERNAL_CONTROL_RECORD_PERMISSIONS,
        ]);
      },
    },
  );
};
